/**
 * Початок дзвінка (components.md, «Цикл дзвінка: реалізація»): «Подзвонити» — справжнє
 * посилання `tel:` без preventDefault; номер копіюється в буфер СИНХРОННО в обробнику жесту,
 * до будь-якого await, інакше Safari відхилить запис без активації користувача.
 *
 * Запис дзвінка в CRM (Г2-W1; calls.md, розд. 4) — `POST /api/v1/calls` з `mode: 'tel'` уже після
 * набору: запит іде з `keepalive` і набір його не чекає (CALL-01: до набору жодного запиту).
 */
import type { CallCreateResponse } from '@clientline/contracts';
import { mark, PERF_MARKS } from '../../perf/marks.ts';
import { api, newKey } from '../../platform/api.ts';

export function startCall(phone: string) {
  mark(PERF_MARKS.callStart);
  try {
    navigator.clipboard?.writeText(phone).catch(() => {
      // Буфер недоступний (немає дозволу, http) — номер усе одно видно в рядку дзвінка.
    });
  } catch {
    // Старі браузери без Clipboard API.
  }
}

/**
 * Посилання `tel:` лише з `+` на початку і цифр. Номер у даних — E.164 (контракт S9), але пробіли,
 * дужки чи дефіси, якщо прийдуть, не мають ламати набір у софтфоні.
 */
export function telHref(phone: string) {
  const plus = phone.trimStart().startsWith('+') ? '+' : '';
  return `tel:${plus}${phone.replace(/\D/g, '')}`;
}

/**
 * Дзвінок `tel:` із запису (calls.md, розд. 4). Номера немає: сервер бере значення поля телефону
 * запису (`phone: { field, index }`) за правами людини.
 */
export interface TelCall {
  host: { entity: string; id: string };
  /** `of: 'root'` — поле кореня господаря за `rollup` (телефон контакту з угоди, calls.md, розд. 4). */
  phone: { field: string; index: number; of?: 'root' };
  /** Дзвінок із задачі-дзвінка: відкрита задача категорії `call` того самого господаря. */
  plan?: string;
}

/** Подвійний клік (UX-18): той самий ключ ідемпотентності на 3 с — сервер поверне перший дзвінок. */
export const DOUBLE_CLICK_MS = 3_000;
const recentKeys = new Map<string, { key: string; at: number }>();

const callId = (call: TelCall) =>
  `${call.host.entity}:${call.host.id}:${call.phone.of ?? 'host'}:${call.phone.field}:${call.phone.index}:${call.plan ?? ''}`;

/** Ключ `Idempotency-Key` дзвінка: повторний клік того самого номера за 3 с — той самий ключ. */
export function telCallKey(call: TelCall, now: number = Date.now()): string {
  const id = callId(call);
  const recent = recentKeys.get(id);
  if (recent && now - recent.at < DOUBLE_CLICK_MS) return recent.key;
  for (const [k, v] of recentKeys) if (now - v.at >= DOUBLE_CLICK_MS) recentKeys.delete(k);
  const key = newKey();
  recentKeys.set(id, { key, at: now });
  return key;
}

/** `POST /api/v1/calls` (`mode: 'tel'`): ключ — з `telCallKey`, повтор після збою — той самий. */
export function createTelCall(call: TelCall, key: string): Promise<CallCreateResponse> {
  return api<CallCreateResponse>('POST', '/api/v1/calls', {
    body: {
      mode: 'tel',
      host: call.host,
      phone: call.phone,
      ...(call.plan ? { plan: call.plan } : {}),
    },
    key,
    keepalive: true,
  });
}
